import axios from 'axios';
import { defineStore } from 'pinia';
import { authStore } from '../stores/auth';

// MIM-2040: `author` is an organisation, not a person — "Mimer", or
// "Mimers Leverantör - <resursgrupp>" when an external contractor answered.
// Odoo decides it and OneCore passes it through, which is why the component
// shows it whole. It used to be the handläggare's name, and MIM-359 cut it to
// the first word to keep surnames from tenants; that is handled at the source
// now, so do not put the truncation back.
// MIM-2036: a close request is posted by the integration on the tenant's
// behalf, so it is shown as the tenant's own message ("Du", orange avatar).
const TENANT_MESSAGE_TYPES = ['from_tenant', 'close_request_from_tenant'];

const transformWorkOrderMessage = (message) => {
    const fromTenant = TENANT_MESSAGE_TYPES.includes(message.messageType);
    return {
        ...message,
        fromTenant,
        authorDisplayName: fromTenant ? 'Du' : message.author,
        createDate: new Date(message.createDate),
    };
};

const transformWorkOrders = (workOrders) =>
    workOrders.map((workOrder) => ({
        ...workOrder,
        // Only Odoo work orders can have a close request; Xpand ones come back false.
        closeRequestPending: workOrder.closeRequestPending === true,
        messages: workOrder.messages.map(transformWorkOrderMessage),
    }));

// The global axios interceptor in main.js resolves 401/403/404/409 to an
// undefined response and turns 500 into a generic prompt, so a store can never
// see those statuses. Calls that must tell a conflict from a failure accept
// every status and check response.status themselves; they also skip the generic
// 500 prompt, so the caller must show its own error.
const acceptAnyStatus = (config) => ({ ...config, validateStatus: () => true });

export const workordersStore = defineStore('workorders-store', {
    state: () => {
        return {
            allWorkorders: '',
            loadAll: false,
            options: null,
            maintenanceUnits: [],
        };
    },

    getters: {
        workorders(state) {
            return state.allWorkorders;
        },
        workorderOptions(state) {
            return state.options;
        },
        workorderMaintenanceUnits(state) {
            return state.maintenanceUnits;
        },
    },

    actions: {
        async fetchWorkorders() {
            return this.loadWorkorders(false);
        },

        async fetchAllWorkorders() {
            this.loadAll = true;
            try {
                return await this.loadWorkorders(true);
            } finally {
                this.loadAll = false;
            }
        },

        // Also used to refresh the list after a close request or an update, so a
        // failure keeps what is already shown instead of emptying the page. The
        // interceptor resolves 401/403/404/409 to undefined and 500 to the error,
        // which ends up in the catch as a TypeError on response.data.
        async loadWorkorders(getAll) {
            try {
                const response = await axios.get(
                    process.env.VUE_APP_API_BASE_URL + 'applicants/me/workorders' + (getAll ? '?getAll=true' : ''),
                    authStore().authConfig,
                );
                this.allWorkorders = transformWorkOrders(response.data.workOrders);
            } catch (err) {
                console.error('Error loading Workorders:', err);
                return err;
            }
        },

        async fetchWorkorderOptions() {
            const response = await axios.get(
                process.env.VUE_APP_API_BASE_URL + 'applicants/me/workOrderOptions',
                authStore().authConfig,
            );
            try {
                // this.options = response.data?.areas.length > 0 ? response.data.areas : workOrderOptions.areas;
                this.options = response.data.areas;
                this.maintenanceUnits = response.data.maintenanceUnits || [];
                if (!this.maintenanceUnits.some((unit) => unit.type === 'Tvättstuga')) {
                    this.options = this.options.map((area) =>
                        area.caption === 'Gemensamma utrymmen'
                            ? {
                                  ...area,
                                  locations: area.locations.filter((location) => location.caption !== 'Tvättstuga'),
                              }
                            : area,
                    );
                }

                const apartmentArea = this.options.find((area) => area.caption === 'I lägenheten');
                if (apartmentArea) {
                    const kitchen = apartmentArea.locations.find((location) => location.code === 'KÖ');
                    if (kitchen) {
                        kitchen.partOfBuildings.push({
                            code: 'MU',
                            caption: 'Microvågsugn',
                        });
                    }
                }
            } catch (err) {
                this.options = [];
                console.error(err);
            }
        },

        async createWorkOrder(workOrder) {
            const {
                rentalObjectCode,
                images,
                email,
                phoneNumber,
                masterKey,
                rows,
                callBetween,
                pet,
                petText,
                hearingImpaired,
            } = workOrder;

            try {
                const response = await axios.post(
                    process.env.VUE_APP_API_BASE_URL + 'workorders/me',
                    {
                        rentalObjectCode,
                        images,
                        accessOptions: {
                            type: masterKey,
                            email,
                            phoneNumber,
                            callBetween: `${callBetween.from} - ${callBetween.to}`,
                        },
                        pet: pet === 'Ja' ? (petText ? petText : 'Ja') : pet === 'Nej' ? 'Nej' : 'Ej angivet',
                        hearingImpaired,
                        rows,
                    },
                    authStore().authConfig,
                );

                if (response.status !== 200) {
                    console.error('Error creating work order:', response.status);
                    return false;
                }

                return (
                    (!response.data.hasOneCoreWorkOrders || response.data.oneCoreSuccess) &&
                    (!response.data.hasXpandWorkOrders || response.data.success)
                );
            } catch (err) {
                console.error('Error creating work order:', err);
                return false;
            }
        },

        // MIM-2036: this asks whoever handles the case to close it; it does not
        // close anything. 409 means a request is already pending, or the case was
        // closed or hidden in the meantime; the API says which in `reason`
        // (already_pending | closed | hidden), absent if it does not know.
        async closeWorkOrder(workOrderId, reason) {
            const trimmedReason = typeof reason === 'string' ? reason.trim() : '';
            try {
                const response = await axios.post(
                    process.env.VUE_APP_API_BASE_URL + 'workorders/close/' + workOrderId,
                    trimmedReason ? { reason: trimmedReason } : {},
                    acceptAnyStatus(authStore().authConfig),
                );

                if (response.status === 204) {
                    return { ok: true };
                }
                console.error('Error requesting close of work order:', response.status);
                const conflict = response.status === 409;
                return { ok: false, conflict, reason: conflict ? response.data?.reason : undefined };
            } catch (err) {
                console.error('Error requesting close of work order:', err);
                return { ok: false, conflict: false };
            }
        },

        // The message is sent as typed: the API serializes the body to core
        // itself now, so it no longer needs pre-escaping here.
        async updateWorkOrder(workOrderId, message) {
            try {
                const response = await axios.post(
                    process.env.VUE_APP_API_BASE_URL + 'workorders/update/' + workOrderId,
                    {
                        message,
                    },
                    acceptAnyStatus(authStore().authConfig),
                );

                if (response.status !== 204) {
                    console.error('Error updating work order:', response.status);
                }
                return { ok: response.status === 204 };
            } catch (err) {
                console.error('Error updating work order:', err);
                return { ok: false };
            }
        },
    },
});

// ------- THESE ARE NECESSARY FOR LOCAL DEVELOPMENT -------

// const workOrderOptions = {
//     areas: [
//         {
//             caption: 'I lägenheten',
//             locations: [
//                 {
//                     code: 'ABU',
//                     caption: 'Altan/Balkong/Uteplats',
//                     partOfBuildings: [
//                         {
//                             code: 'EL',
//                             caption: 'El',
//                         },
//                         {
//                             code: 'GRM',
//                             caption: 'Gräsmatta',
//                         },
//                         {
//                             code: 'IG',
//                             caption: 'Inglasning - Balkong, uteplats',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                         {
//                             code: 'STA',
//                             caption: 'Staket',
//                         },
//                         {
//                             code: 'ABD',
//                             caption: 'Altan eller balkongdörr',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'BWC',
//                     caption: 'Badrum/WC',
//                     partOfBuildings: [
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'BLB',
//                             caption: 'Blandare, badkar',
//                         },
//                         {
//                             code: 'BLD',
//                             caption: 'Blandare, dusch',
//                         },
//                         {
//                             code: 'BLT',
//                             caption: 'Blandare, tvättställ',
//                         },
//                         {
//                             code: 'DUDS',
//                             caption: 'Duschdrapperistång',
//                         },
//                         {
//                             code: 'DUMS',
//                             caption: 'Duschmunstycke',
//                         },
//                         {
//                             code: 'DUSL',
//                             caption: 'Duschslang',
//                         },
//                         {
//                             code: 'GB',
//                             caption: 'Golvbrunn',
//                         },
//                         {
//                             code: 'HDT',
//                             caption: 'Handdukstork',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'SB',
//                             caption: 'Strömbrytare',
//                         },
//                         {
//                             code: 'EL',
//                             caption: 'El',
//                         },
//                         {
//                             code: 'BDS',
//                             caption: 'Badrumsskåp',
//                         },
//                         {
//                             code: 'TM',
//                             caption: 'Tvättmaskin',
//                         },
//                         {
//                             code: 'TO',
//                             caption: 'Toalettstol',
//                         },
//                         {
//                             code: 'TS',
//                             caption: 'Tvättställ',
//                         },
//                         {
//                             code: 'TT',
//                             caption: 'Torktumlare',
//                         },
//                         {
//                             code: 'VENT',
//                             caption: 'Ventilation ',
//                         },
//                         {
//                             code: 'ÖV ',
//                             caption: 'Övrigt ',
//                         },
//                         {
//                             code: 'VÄRM',
//                             caption: 'Värme ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'FÖR',
//                     caption: 'Förråd',
//                     partOfBuildings: [
//                         {
//                             code: 'EL',
//                             caption: 'El ',
//                         },
//                         {
//                             code: 'SKAD',
//                             caption: 'Skadegörelse',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'ÖV ',
//                             caption: 'Övrigt ',
//                         },
//                         {
//                             code: 'SD ',
//                             caption: 'Skadedjur ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'KÖ',
//                     caption: 'Kök',
//                     partOfBuildings: [
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'GO',
//                             caption: 'Golv',
//                         },
//                         {
//                             code: 'BLK',
//                             caption: 'Blandare, kök',
//                         },
//                         {
//                             code: 'DB',
//                             caption: 'Diskbänk',
//                         },
//                         {
//                             code: 'ELU',
//                             caption: 'Eluttag',
//                         },
//                         {
//                             code: 'FL',
//                             caption: 'Fläkt',
//                         },
//                         {
//                             code: 'SB',
//                             caption: 'Strömbrytare',
//                         },
//                         {
//                             code: 'SP',
//                             caption: 'Spis/ugn',
//                         },
//                         {
//                             code: 'DM',
//                             caption: 'Diskmaskin',
//                         },
//                         {
//                             code: 'KY',
//                             caption: 'Kyl',
//                         },
//                         {
//                             code: 'FR',
//                             caption: 'Frys',
//                         },
//                         {
//                             code: 'KF',
//                             caption: 'Kyl/frys',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'VA',
//                             caption: 'Vatten',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'HALL',
//                     caption: 'Hall',
//                     partOfBuildings: [
//                         {
//                             code: 'GA',
//                             caption: 'Garderob',
//                         },
//                         {
//                             code: 'BR',
//                             caption: 'Brandvarnare',
//                         },
//                         {
//                             code: 'HA',
//                             caption: 'Hatthylla',
//                         },
//                         {
//                             code: 'ELU',
//                             caption: 'Eluttag',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'VARD',
//                     caption: 'Vardagsrum',
//                     partOfBuildings: [
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'GO',
//                             caption: 'Golv',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'TA',
//                             caption: 'Tak',
//                         },
//                         {
//                             code: 'SB',
//                             caption: 'Strömbrytare',
//                         },
//                         {
//                             code: 'ELU',
//                             caption: 'Eluttag',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'SOV',
//                     caption: 'Sovrum',
//                     partOfBuildings: [
//                         {
//                             code: 'GA',
//                             caption: 'Garderob',
//                         },
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'GO',
//                             caption: 'Golv',
//                         },
//                         {
//                             code: 'ID',
//                             caption: 'Innerdörr',
//                         },
//                         {
//                             code: 'ELE',
//                             caption: 'Element',
//                         },
//                         {
//                             code: 'SB',
//                             caption: 'Strömbrytare',
//                         },
//                         {
//                             code: 'ELU',
//                             caption: 'Eluttag',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'LGH ',
//                     caption: 'Hela lägenheten ',
//                     partOfBuildings: [
//                         {
//                             code: 'VÄ',
//                             caption: 'Värme ',
//                         },
//                         {
//                             code: 'SD ',
//                             caption: 'Skadedjur ',
//                         },
//                         {
//                             code: 'VENT',
//                             caption: 'Ventilation ',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//             ],
//         },
//         {
//             caption: 'Bilplatskontrakt ',
//             locations: [
//                 {
//                     code: 'PP',
//                     caption: 'Parkeringsplats ',
//                     partOfBuildings: [
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                         {
//                             code: 'MV',
//                             caption: 'Motorvärmaruttag',
//                         },
//                     ],
//                 },
//             ],
//         },
//         {
//             caption: 'Gemensamma utrymmen',
//             locations: [
//                 {
//                     code: 'MB',
//                     caption: 'Miljöbod',
//                     partOfBuildings: [
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning ',
//                         },
//                         {
//                             code: 'YD',
//                             caption: 'Ytterdörr',
//                         },
//                         {
//                             code: 'STÄD',
//                             caption: 'Städning',
//                         },
//                         {
//                             code: 'TÖM',
//                             caption: 'Tömning',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'TV',
//                     caption: 'Tvättstuga',
//                     partOfBuildings: [
//                         {
//                             code: 'FÖ',
//                             caption: 'Fönster',
//                         },
//                         {
//                             code: 'GB',
//                             caption: 'Golvbrunn',
//                         },
//                         {
//                             code: 'MA',
//                             caption: 'Mangel',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                         {
//                             code: 'TÅ',
//                             caption: 'Torkskåp',
//                         },
//                         {
//                             code: 'TM',
//                             caption: 'Tvättmaskin',
//                         },
//                         {
//                             code: 'TT',
//                             caption: 'Torktumlare',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'TH',
//                     caption: 'Trapphus',
//                     partOfBuildings: [
//                         {
//                             code: 'STÄD',
//                             caption: 'Städning',
//                         },
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                         {
//                             code: 'YD',
//                             caption: 'Ytterdörr',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'VI ',
//                     caption: 'Vind ',
//                     partOfBuildings: [
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'DÖR',
//                             caption: 'Dörr',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'KÄL',
//                     caption: 'Källare',
//                     partOfBuildings: [
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'DJUR',
//                             caption: 'Skadedjur',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                         {
//                             code: 'SKAD',
//                             caption: 'Skadegörelse',
//                         },
//                         {
//                             code: 'STÄD',
//                             caption: 'Städning',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'CYF',
//                     caption: 'Cykelförråd ',
//                     partOfBuildings: [
//                         {
//                             code: 'BEL',
//                             caption: 'Belysning',
//                         },
//                         {
//                             code: 'DÖR',
//                             caption: 'Dörr',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt ',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'ÖV',
//                     caption: 'Övrigt',
//                     partOfBuildings: [
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                     ],
//                 },
//                 {
//                     code: 'UTE',
//                     caption: 'Gården/utomhus',
//                     partOfBuildings: [
//                         {
//                             code: 'LEK',
//                             caption: 'Lekplats',
//                         },
//                         {
//                             code: 'GRM',
//                             caption: 'Gräsmatta',
//                         },
//                         {
//                             code: 'GM',
//                             caption: 'Gröna Mimer',
//                         },
//                         {
//                             code: 'SNÖ',
//                             caption: 'Snöröjning/sandning',
//                         },
//                         {
//                             code: 'ÖV',
//                             caption: 'Övrigt',
//                         },
//                     ],
//                 },
//             ],
//         },
//     ],
// };
